import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {greetWithoutTitle} from '../../../../../../utility/parentGreeting';
import DOMPurify from 'dompurify';
import {useSelector} from 'react-redux';
import {useTheme} from '@mui/material/styles';
import {Alert, Box} from '@mui/material';
import FORM_AUDIENCE_TYPE from '../../../../../../constants/FormAudienceType';
import FORM_ASSIGNMENT_TYPE from '../../../../../../constants/FormAssignmentType';

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getRenderablePlaceholderValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') return null;

    const normalizedValue = String(value).trim();

    if (normalizedValue === '' || normalizedValue.startsWith('@')) {
        return null;
    }

    return normalizedValue;
};

const replacePlaceholder = (originalText, placeholder, value, removeWhenMissing) => {
    const renderableValue = getRenderablePlaceholderValue(value);
    const regex = new RegExp(escapeRegExp(placeholder), 'g');

    if (renderableValue !== null) {
        return originalText.replace(regex, renderableValue);
    }

    return removeWhenMissing
        ? originalText.replace(regex, '')
        : originalText;
};

const replaceLegacyPlaceholder = (originalText, placeholder, value, removeWhenMissing) => {
    const renderableValue = getRenderablePlaceholderValue(value);

    if (placeholder === '@Parent') {
        const regex = /@Parent(?![A-Za-z])/g;
        if (renderableValue !== null) return originalText.replace(regex, renderableValue);
        return removeWhenMissing ? originalText.replace(regex, '') : originalText;
    }

    if (placeholder === '@Employee') {
        const regex = /@Employee(?![A-Za-z])/g;
        if (renderableValue !== null) return originalText.replace(regex, renderableValue);
        return removeWhenMissing ? originalText.replace(regex, '') : originalText;
    }

    return originalText;
};

const ALL_PLACEHOLDERS = [
    '@SchoolName',

    '@StudentForename',
    '@StudentFullName',
    '@HisHer',
    '@HeShe',
    '@HimHer',

    '@StudentEmail',
    '@StudentUPN',
    '@StudentDOB',
    '@StudentSex',
    '@StudentYearGroup',
    '@StudentForm',
    '@StudentHouse',
    '@StudentBoarderStatus',
    '@StudentAddress',
    '@StudentLegalForename',
    '@StudentLegalFullName',
    '@PreviousSchoolName',
    '@PreviousSchoolEmail',
    '@StudentApplicationStatus',
    '@StudentIntakeGroup',

    '@ParentFullName',
    '@ParentSurname',
    '@ParentEmail',
    '@ParentPhone',
    '@ParentAddress',

    '@EmployeeFullName',
    '@EmployeeForename',
    '@EmployeeLegalForename',
    '@EmployeeSurname',
    '@EmployeeEmail',
    '@EmployeePhone',
    '@EmployeeDOB',
    '@EmployeeAddress',
    '@EmployeeLegalSurname',
    '@EmployeeLegalFullName',

    '@Title',
];

const LEGACY_PLACEHOLDER_REGEX = {
    '@Parent': /@Parent(?![A-Za-z])/,
    '@Employee': /@Employee(?![A-Za-z])/,
};

const STUDENT_PLACEHOLDERS = new Set([
    '@StudentForename',
    '@StudentFullName',
    '@HisHer',
    '@HeShe',
    '@HimHer',
    '@StudentEmail',
    '@StudentUPN',
    '@StudentDOB',
    '@StudentSex',
    '@StudentYearGroup',
    '@StudentForm',
    '@StudentHouse',
    '@StudentBoarderStatus',
    '@StudentAddress',
    '@StudentLegalForename',
    '@StudentLegalFullName',
    '@PreviousSchoolName',
    '@PreviousSchoolEmail',
    '@StudentApplicationStatus',
    '@StudentIntakeGroup',
]);

const PARENT_PLACEHOLDERS = new Set([
    '@ParentFullName',
    '@ParentSurname',
    '@ParentEmail',
    '@ParentPhone',
    '@ParentAddress',
    '@Parent', // legacy
    '@Title',
]);

const EMPLOYEE_PLACEHOLDERS = new Set([
    '@EmployeeFullName',
    '@EmployeeForename',
    '@EmployeeLegalForename',
    '@EmployeeSurname',
    '@EmployeeEmail',
    '@EmployeePhone',
    '@EmployeeDOB',
    '@EmployeeAddress',
    '@EmployeeLegalSurname',
    '@EmployeeLegalFullName',
    '@Employee', // legacy
    '@Title',
]);

const LINKED_EMPLOYEE_PLACEHOLDERS = new Set([
    '@LinkedEmployeeFullName',
    '@LinkedEmployeeForename',
    '@LinkedEmployeeLegalForename',
    '@LinkedEmployeeSurname',
    '@LinkedEmployeeLegalFullName',
    '@LinkedEmployeeLegalSurname',
    '@LinkedEmployeeEmail',
    '@LinkedEmployeePhone',
    '@LinkedEmployeeDOB',
    '@LinkedEmployeeAddress',
]);

const SHARED_PLACEHOLDERS = new Set(['@SchoolName']);

const normaliseRecipientType = (recipientType) => {
    if (Number(recipientType) === FORM_ASSIGNMENT_TYPE.students) return 'student';
    if (Number(recipientType) === FORM_ASSIGNMENT_TYPE.employees) return 'employee';
    return String(recipientType ?? '').toLowerCase();
};

const shouldIncludeParentPlaceholders = (triggerSourceAudience) =>
    Number(triggerSourceAudience) !== FORM_AUDIENCE_TYPE.externalUsers;
const getTriggerSourceAudience = (placeholderValues, triggerSourceAudience) =>
    placeholderValues?.placeholderMetadata?.triggerSourceAudience ?? triggerSourceAudience;

const getAllowedPlaceholders = (
    audience,
    recipientType,
    linkedToStudents = false,
    triggerSourceAudience = null,
    linkedToEmployees = false) => {
    const allowed = new Set(SHARED_PLACEHOLDERS);

    const addAll = (setToAdd) => setToAdd.forEach(t => allowed.add(t));

    switch (audience) {
        case FORM_AUDIENCE_TYPE.students:
            addAll(STUDENT_PLACEHOLDERS);
            return allowed;

        case FORM_AUDIENCE_TYPE.enquiries:
            addAll(STUDENT_PLACEHOLDERS);
            addAll(PARENT_PLACEHOLDERS);
            return allowed;

        case FORM_AUDIENCE_TYPE.parentsForChildCompletion:
            addAll(STUDENT_PLACEHOLDERS);
            addAll(PARENT_PLACEHOLDERS);
            return allowed;

        case FORM_AUDIENCE_TYPE.previousSchool:
            addAll(STUDENT_PLACEHOLDERS);
            return allowed;

        case FORM_AUDIENCE_TYPE.employees:
            addAll(EMPLOYEE_PLACEHOLDERS);
            if (linkedToStudents) {
                addAll(STUDENT_PLACEHOLDERS);
            }
            if (linkedToEmployees) {
                addAll(LINKED_EMPLOYEE_PLACEHOLDERS);
            }
            return allowed;

        case FORM_AUDIENCE_TYPE.externalUsers: {
            const rt = normaliseRecipientType(recipientType);

            if (!rt) return allowed;

            if (rt === 'student') {
                addAll(STUDENT_PLACEHOLDERS);
                if (shouldIncludeParentPlaceholders(triggerSourceAudience)) {
                    addAll(PARENT_PLACEHOLDERS);
                }
            }
            if (rt === 'employee') addAll(EMPLOYEE_PLACEHOLDERS);

            return allowed;
        }

        default:
            return allowed;
    }
};

const detectUsedPlaceholders = (text) => {
    if (!text) return [];

    const used = new Set();

    ALL_PLACEHOLDERS.forEach((t) => {
        if (text.includes(t)) used.add(t);
    });

    Object.entries(LEGACY_PLACEHOLDER_REGEX).forEach(([placeholder, regex]) => {
        if (regex.test(text)) used.add(placeholder);
    });

    return Array.from(used);
};

const getAudienceLabel = (audience) => {
    switch (audience) {
        case FORM_AUDIENCE_TYPE.students:
            return 'Students';
        case FORM_AUDIENCE_TYPE.enquiries:
            return 'Enquiries';
        case FORM_AUDIENCE_TYPE.parentsForChildCompletion:
            return 'Parents';
        case FORM_AUDIENCE_TYPE.employees:
            return 'Employees';
        case FORM_AUDIENCE_TYPE.previousSchool:
            return 'Previous school';
        case FORM_AUDIENCE_TYPE.externalUsers:
            return 'External users';
        default:
            return '';
    }
};

const getRecipientTypeLabel = (recipientType) => {
    if (!recipientType) return '';
    const v = String(recipientType).toLowerCase();
    if (v === 'student') return 'Student';
    if (v === 'employee') return 'Employee';
    return recipientType;
};

const RichEditorResultText = ({
                                  id,
                                  description,
                                  placeholderValues,
                                  enableValidation,
                                  error,
                                  audience,
                                  recipientType,
                                  linkedToStudents = false,
                                  linkedToEmployees = false,
                                  triggerSourceAudience = null,
                                  readOnly,
                                  submitMode,
                              }) => {
    const {account} = useSelector(state => state.application);
    const [text, setText] = useState('');
    const theme = useTheme();

    const isAudienceUnset = (a) =>
        a === null ||
        a === undefined ||
        a === FORM_AUDIENCE_TYPE.none;

    const effectiveTriggerSourceAudience = getTriggerSourceAudience(placeholderValues, triggerSourceAudience);

    const invalidPlaceholders = useMemo(() => {
        if (readOnly || submitMode) return [];
        if (!description) return [];
        if (audience === null || audience === undefined) return [];

        const used = detectUsedPlaceholders(description);
        if (!used.length) return [];

        const allowed = getAllowedPlaceholders(
            audience,
            recipientType,
            linkedToStudents,
            effectiveTriggerSourceAudience,
            linkedToEmployees
        );
        return used.filter(t => !allowed.has(t));
    }, [
        description,
        audience,
        recipientType,
        linkedToStudents,
        linkedToEmployees,
        effectiveTriggerSourceAudience,
        readOnly,
        submitMode
    ]);

    const invalidPlaceholderMessage = useMemo(() => {
        if (!invalidPlaceholders.length) return '';

        const audienceLabel = `${getAudienceLabel(audience)} audience`;

        if (audience === FORM_AUDIENCE_TYPE.externalUsers) {
            const rt = String(recipientType ?? '').trim();

            if (!rt) {
                return `This text contains placeholders that do not match the selected ${audienceLabel}. Select a recipient type to validate placeholders properly: ${invalidPlaceholders.join(', ')}.`;
            }

            return `This text contains placeholders that do not match the selected ${audienceLabel} (recipient type: ${getRecipientTypeLabel(recipientType)}): ${invalidPlaceholders.join(', ')}.`;
        }

        return `This text contains placeholders that do not match the selected ${audienceLabel}: ${invalidPlaceholders.join(', ')}.`;
    }, [invalidPlaceholders, audience, recipientType]);

    useEffect(() => {
        let resultText = description ?? '';

        const shouldProcessPlaceholders = submitMode;
        const removeWhenMissing = shouldProcessPlaceholders;

        if (shouldProcessPlaceholders && placeholderValues) {
            resultText = replacePlaceholder(resultText, '@SchoolName', placeholderValues.schoolName, removeWhenMissing);

            resultText = replacePlaceholder(resultText, '@StudentForename', placeholderValues.studentForename, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentFullName', placeholderValues.studentFullName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@HisHer', placeholderValues.hisHer, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@HeShe', placeholderValues.heShe, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@HimHer', placeholderValues.himHer, removeWhenMissing);

            resultText = replacePlaceholder(resultText, '@StudentEmail', placeholderValues.studentEmail, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentUPN', placeholderValues.studentUPN, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentDOB', placeholderValues.studentDOB, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentSex', placeholderValues.studentSex, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentYearGroup', placeholderValues.studentYearGroup, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentForm', placeholderValues.studentForm, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentHouse', placeholderValues.studentHouse, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentBoarderStatus', placeholderValues.studentBoarderStatus, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentAddress', placeholderValues.studentAddress, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentLegalForename', placeholderValues.studentLegalForename, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentLegalFullName', placeholderValues.studentLegalFullName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@PreviousSchoolName', placeholderValues.previousSchoolName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@PreviousSchoolEmail', placeholderValues.previousSchoolEmail, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentApplicationStatus', placeholderValues.studentApplicationStatus, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentIntakeGroup', placeholderValues.studentIntakeGroup, removeWhenMissing);

            resultText = greetWithoutTitle(resultText, placeholderValues);
            resultText = replacePlaceholder(resultText, '@ParentFullName', placeholderValues.parentFullName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@ParentSurname', placeholderValues.parentSurname, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@ParentEmail', placeholderValues.parentEmail, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@ParentPhone', placeholderValues.parentPhone, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@ParentAddress', placeholderValues.parentAddress, removeWhenMissing);
            resultText = replaceLegacyPlaceholder(resultText, '@Parent', placeholderValues.parentFullName ?? placeholderValues.parent, removeWhenMissing);

            resultText = replacePlaceholder(resultText, '@EmployeeFullName', placeholderValues.employeeFullName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeForename', placeholderValues.employeeForename, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeLegalForename', placeholderValues.employeeLegalForename, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeSurname', placeholderValues.employeeSurname, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeEmail', placeholderValues.employeeEmail, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeePhone', placeholderValues.employeePhone, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeDOB', placeholderValues.employeeDOB, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeAddress', placeholderValues.employeeAddress, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeLegalSurname', placeholderValues.employeeLegalSurname, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeLegalFullName', placeholderValues.employeeLegalFullName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@LinkedEmployeeFullName', placeholderValues.linkedEmployeeFullName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@LinkedEmployeeForename', placeholderValues.linkedEmployeeForename, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@LinkedEmployeeLegalForename', placeholderValues.linkedEmployeeLegalForename, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@LinkedEmployeeSurname', placeholderValues.linkedEmployeeSurname, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@LinkedEmployeeLegalFullName', placeholderValues.linkedEmployeeLegalFullName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@LinkedEmployeeLegalSurname', placeholderValues.linkedEmployeeLegalSurname, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@LinkedEmployeeEmail', placeholderValues.linkedEmployeeEmail, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@LinkedEmployeePhone', placeholderValues.linkedEmployeePhone, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@LinkedEmployeeDOB', placeholderValues.linkedEmployeeDOB, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@LinkedEmployeeAddress', placeholderValues.linkedEmployeeAddress, removeWhenMissing);
            resultText = replaceLegacyPlaceholder(resultText, '@Employee', placeholderValues.employeeFullName ?? placeholderValues.employee, removeWhenMissing);

            resultText = replacePlaceholder(resultText, '@Title', placeholderValues.title, removeWhenMissing);
        }

        if (shouldProcessPlaceholders && removeWhenMissing) {
            ALL_PLACEHOLDERS.forEach((t) => {
                resultText = resultText.replace(new RegExp(escapeRegExp(t), 'g'), '');
            });

            resultText = resultText.replace(/@Parent(?![A-Za-z])/g, '');
            resultText = resultText.replace(/@Employee(?![A-Za-z])/g, '');
        }

        setText(DOMPurify.sanitize(resultText));
    }, [id, account, placeholderValues, readOnly, submitMode, description]);

    return (
        <div
            style={{
                alignItems: 'center',
                // AD-2689: no empty space under a question whose text has not been written yet.
                paddingBottom: text ? '12px' : 0,
            }}
        >
            {!submitMode && !readOnly && !isAudienceUnset(audience) && invalidPlaceholders.length > 0 && (
                <Alert severity="warning" sx={{mb: 1}}>
                    {invalidPlaceholderMessage}
                </Alert>
            )}

            <Box
                sx={{
                    ...theme.typography.body1,
                    color: theme.palette.text.primary,
                }}
            >
                <span dangerouslySetInnerHTML={{__html: text}}/>
                {enableValidation && (
                    <Box
                        component="span"
                        color={error ? theme.palette.error.main : theme.palette.text.primary}
                        style={{
                            alignSelf: 'flex-start',
                            marginLeft: 5
                        }}
                    >*</Box>
                )}
            </Box>
        </div>
    );
};

RichEditorResultText.defaultProps = {
    error: false,
    submitMode: false,
    audience: undefined,
    recipientType: undefined,
    placeholderValues: null,
    readOnly: false,
    readonly: undefined,
};

RichEditorResultText.propTypes = {
    id: PropTypes.string,
    description: PropTypes.string.isRequired,
    readOnly: PropTypes.bool,
    readonly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    enableValidation: PropTypes.bool,
    error: PropTypes.bool,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    triggerSourceAudience: PropTypes.number,
    submitMode: PropTypes.bool.isRequired,
};

/**
 * Option labels are plain text rather than rich text, so they never pass through the editor's own
 * placeholder handling. A public form may only carry @SchoolName, so that is all this swaps.
 */
export const withSchoolName = (text, placeholderValues) =>
    typeof text === 'string' && placeholderValues?.schoolName
        ? text.replace(/@SchoolName/g, placeholderValues.schoolName)
        : text;

export default RichEditorResultText;
